"use client"

import { useCallback, useEffect, useState } from "react"
import { ArrowLeftMini, ChatBubbleLeftRight } from "@medusajs/icons"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

import type {
  Conversation,
  ConversationMessage,
} from "@lib/data/conversations"
import {
  getConversationWithMessages,
  listConversations,
  markConversationRead,
} from "@lib/data/conversations"

import ConversationList from "@modules/account/components/conversation-list"
import MessageThread from "@modules/account/components/message-thread"

type Thread = {
  conversation: Conversation
  messages: ConversationMessage[]
}

type Props = {
  /** Called when the customer closes the thread on mobile (back to the list). */
  onBack?: () => void
  /** Conversation to select and open right away, e.g. right after creating one. */
  initialConversationId?: string | null
  /** Element in the sheet's top strip where the thread header is portaled. */
  headerSlot?: HTMLElement | null
}

const sortByActivity = (conversations: Conversation[]) =>
  [...conversations].sort((a, b) => {
    const ta = a.last_message_at ?? a.created_at
    const tb = b.last_message_at ?? b.created_at
    return tb.localeCompare(ta)
  })

const ChatPanel = ({ onBack, initialConversationId, headerSlot }: Props) => {
  const t = useTranslations("AccountMessages")
  const [conversations, setConversations] = useState<Conversation[] | null>(
    null
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [thread, setThread] = useState<Thread | null>(null)
  const [loadingThread, setLoadingThread] = useState(false)

  useEffect(() => {
    let active = true
    void listConversations().then((list) => {
      if (!active) return
      setConversations(sortByActivity(list))
      if (initialConversationId && list.some((c) => c.id === initialConversationId)) {
        setSelectedId(initialConversationId)
        setLoadingThread(true)
        void getConversationWithMessages(initialConversationId).then((data) => {
          if (!active) return
          setLoadingThread(false)
          if (!data) return
          setThread({
            conversation: withSellerName(data.conversation, initialConversationId),
            messages: data.messages,
          })
          markConversationRead(initialConversationId)
        })
      }
    })
    return () => {
      active = false
    }
  }, [initialConversationId])

  const refreshConversations = useCallback(async () => {
    const list = await listConversations()
    setConversations(sortByActivity(list))
  }, [])

  // The plugin's thread route carries no seller name, so copy it from the
  // enriched list entry (listConversations fills it from the previews call).
  const withSellerName = (
    conversation: Conversation,
    id: string
  ): Conversation => ({
    ...conversation,
    seller_name:
      conversation.seller_name ??
      conversations?.find((c) => c.id === id)?.seller_name ??
      null,
  })

  const select = useCallback(
    async (id: string) => {
      setSelectedId(id)
      setThread(null)
      setLoadingThread(true)
      const data = await getConversationWithMessages(id)
      setLoadingThread(false)
      if (!data) return
      setThread({
        conversation: withSellerName(data.conversation, id),
        messages: data.messages,
      })
      // Fire-and-forget: mark the thread as read when the customer opens it.
      markConversationRead(id)
      setConversations((prev) =>
        prev
          ? prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c))
          : prev
      )
    },
    // withSellerName reads the current conversations list for the seller name.
    [conversations]
  )

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    } else {
      setSelectedId(null)
      setThread(null)
    }
  }, [onBack])

  const conversation = thread?.conversation

  const threadTitle = conversation
    ? conversation.order_display_id
      ? t("conversation.withSeller", {
          seller: conversation.seller_name ?? conversation.order_display_id,
        })
      : conversation.seller_name
        ? t("conversation.withSeller", { seller: conversation.seller_name })
        : conversation.subject
          ? t("conversation.subject", { subject: conversation.subject })
          : ""
    : ""

  return (
    <div className="flex h-full min-h-0">
      {/* Thread header, portaled into the sheet's top strip next to the
      close button (see ChatModal). Rendered only while a thread is open. */}
      {headerSlot && selectedId
        ? createPortal(
            <>
              <button
                type="button"
                onClick={handleBack}
                aria-label={t("backToConversations")}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ui-fg-subtle transition-fg hover:bg-ui-bg-subtle hover:text-ui-fg-base sm:hidden"
              >
                <ArrowLeftMini width={18} height={18} />
              </button>
              <p className="min-w-0 flex-1 truncate font-medium text-ui-fg-base">
                {threadTitle}
              </p>
            </>,
            headerSlot
          )
        : null}
      {/* List pane */}
      <div
        className={cn(
          "flex w-full flex-col overflow-y-auto sm:flex sm:w-72 sm:shrink-0 sm:border-r sm:border-ui-border-base",
          selectedId && "hidden sm:flex"
        )}
      >
        {conversations === null ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <div
              className="size-6 animate-spin rounded-full border-2 border-ui-border-base border-t-ui-fg-interactive"
              aria-hidden="true"
            />
          </div>
        ) : (
          <ConversationList
            conversations={conversations}
            onSelect={select}
            selectedId={selectedId}
          />
        )}
      </div>

      {/* Thread pane */}
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          !selectedId && "hidden sm:flex"
        )}
      >
        {selectedId ? (
          <>
            {loadingThread || !thread ? (
              <div className="flex flex-1 items-center justify-center">
                <div
                  className="size-6 animate-spin rounded-full border-2 border-ui-border-base border-t-ui-fg-interactive"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <div className="min-h-0 flex-1">
                <MessageThread
                  conversation={thread.conversation}
                  initialMessages={thread.messages}
                  onSent={refreshConversations}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ui-bg-subtle">
              <ChatBubbleLeftRight
                width={24}
                height={24}
                className="text-ui-fg-subtle"
              />
            </div>
            <p className="txt-small text-ui-fg-subtle">
              {t("selectConversation")}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ChatPanel
